// CoinManagement.js
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getSocket } from "../utils/socket";
import { FaCoins } from "react-icons/fa";
import { motion } from "framer-motion";

const CoinManagement = () => {
  const { fetchWithRetry, user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [coinBalance, setCoinBalance] = useState(0);
  const [coinTransactions, setCoinTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationMessage, setNotificationMessage] = useState(null);

  // State cho quản lý rút coin
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [withdrawalMethod, setWithdrawalMethod] = useState("");
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [newPaymentGateway, setNewPaymentGateway] = useState("");
  const [newPaymentAccountDetails, setNewPaymentAccountDetails] = useState("");
  const [currentMembership, setCurrentMembership] = useState(null);

  // State cho tính năng đổi coin thành gói VIP
  const [plans, setPlans] = useState([]);
  const [activeTab, setActiveTab] = useState("management");

  // State cho lịch sử yêu cầu nâng cấp
  const [upgradeRequests, setUpgradeRequests] = useState([]);

  // Hàm xác định cấp bậc của gói VIP
  const getPlanLevel = (planName) => {
    if (!planName) return 0;
    switch (planName.toLowerCase()) {
      case "thành viên thường": return 1;
      case "vàng": return 2;
      case "bạch kim": return 3;
      case "kim cương": return 4;
      case "thành viên cao cấp": return 5;
      default: return 0;
    }
  };

  const fetchCoinBalance = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-balance" });
      setCoinBalance(res.data.coinBalance || 0);
    } catch (error) {
      console.error("Lỗi khi lấy số dư coin:", error);
      setError("Không thể lấy số dư coin. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchCoinTransactions = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-transactions" });
      setCoinTransactions(res.data || []);
    } catch (error) {
      console.error("Lỗi khi lấy lịch sử giao dịch coin:", error);
      setError("Không thể lấy lịch sử giao dịch coin. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchPaymentAccounts = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/payment-accounts" });
      setPaymentAccounts(res.data || []);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách tài khoản thanh toán:", error);
      setError("Không thể lấy danh sách tài khoản thanh toán. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchPaymentGateways = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/supported-payment-gateways" });
      if (res.data.message && res.data.data.length === 0) {
        setError(res.data.message);
      }
      setPaymentGateways(res.data.data || res.data || []);
    } catch (error) {
      console.error("Lỗi khi lấy danh sách cổng thanh toán:", error);
      setError(error.response?.data?.message || "Không thể lấy danh sách cổng thanh toán. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchExchangeRate = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-exchange-rate?currency=VND" });
      setExchangeRate(res.data.rate || 0);
      if (res.data.message && res.data.rate === 0) {
        setError(res.data.message);
      }
    } catch (error) {
      console.error("Lỗi khi lấy giá trị quy đổi:", error);
      if (!error.response?.data?.message) {
        setError("Không thể lấy giá trị quy đổi. Vui lòng liên hệ quản trị viên!");
      }
    }
  }, [fetchWithRetry]);

  const fetchCoinToVipPlans = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-to-vip-plans" });
      if (Array.isArray(res.data)) {
        const validPlans = res.data.filter(plan => plan.coinPrice && plan.coinPrice > 0);
        console.log("Danh sách gói VIP sau khi lọc:", validPlans);
        setPlans(validPlans);
      } else {
        setPlans([]);
        setError("Dữ liệu gói VIP không hợp lệ. Vui lòng liên hệ quản trị viên!");
      }
    } catch (error) {
      console.error("Lỗi khi lấy danh sách gói VIP đổi bằng coin:", error);
      setError("Không thể lấy danh sách gói VIP đổi bằng coin. Vui lòng thử lại sau!");
      setPlans([]);
    }
  }, [fetchWithRetry]);

  const fetchCurrentMembership = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/current-membership" });
      setCurrentMembership(res.data.membership || null);
    } catch (error) {
      console.error("Lỗi khi lấy thông tin gói hiện tại:", error);
      setError("Không thể lấy thông tin gói hiện tại. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const fetchUpgradeRequests = useCallback(async () => {
    try {
      const res = await fetchWithRetry({ method: "get", url: "/api/users/coin-to-vip-requests" });
      setUpgradeRequests(res.data || []);
    } catch (error) {
      console.error("Lỗi khi lấy lịch sử yêu cầu nâng cấp gói VIP:", error);
      setError("Không thể lấy lịch sử yêu cầu nâng cấp gói VIP. Vui lòng thử lại sau!");
    }
  }, [fetchWithRetry]);

  const handleUpgradeWithCoin = async (planId, planName, coinPrice) => {
    const confirmMessage = currentMembership && currentMembership.status === "active" && new Date(currentMembership.endDate) > new Date()
      ? `Bạn đang có gói VIP "${currentMembership.plan?.name}" còn hiệu lực đến ${new Date(currentMembership.endDate).toLocaleDateString("vi-VN")}. Nếu nâng cấp lên gói "${planName}", gói hiện tại sẽ bị thay thế. Bạn có muốn tiếp tục?`
      : `Bạn có muốn sử dụng ${coinPrice} coin để nâng cấp lên gói ${planName}?`;
    
    if (!window.confirm(confirmMessage)) return;
    try {
      const res = await fetchWithRetry({
        method: "post",
        url: "/api/users/upgrade-membership-with-coin",
        data: { planId },
      });
      setNotificationMessage(res.data.message);
      setTimeout(() => setNotificationMessage(null), 3000);
      await fetchCoinBalance();
      await fetchCoinTransactions();
      await fetchCurrentMembership();
      await fetchUpgradeRequests();
    } catch (error) {
      setError(error.response?.data?.message || "Lỗi khi nâng cấp gói bằng coin!");
    }
  };

  useEffect(() => {
    const socket = getSocket();
    socket.on("notification", (notification) => {
      if (notification.type === "withdrawal_request") {
        setNotificationMessage(notification.message || "Yêu cầu rút tiền của bạn đã được gửi!");
      } else if (notification.type === "withdrawal_status") {
        setNotificationMessage(notification.message || "Yêu cầu rút tiền của bạn đã được xử lý!");
        fetchCoinBalance();
        fetchCoinTransactions();
      } else if (notification.type === "payment_gateway_updated") {
        setNotificationMessage(notification.message || "Danh sách cổng thanh toán đã được cập nhật!");
        fetchPaymentGateways();
        setTimeout(() => setNotificationMessage(null), 3000);
      } else if (notification.type === "exchange_rate_updated") {
        setNotificationMessage(notification.message || "Giá trị quy đổi đã được cập nhật!");
        fetchExchangeRate();
      } else if (notification.type === "membership_upgrade") {
        setNotificationMessage(notification.message || "Tài khoản của bạn đã được nâng cấp!");
        fetchCoinBalance();
        fetchCoinTransactions();
        fetchCurrentMembership();
        fetchUpgradeRequests();
      } else if (notification.type === "membership_upgrade_status") {
        setNotificationMessage(notification.message || "Yêu cầu nâng cấp gói VIP của bạn đã được xử lý!");
        fetchUpgradeRequests();
      }
    });

    socket.on("coinBalanceUpdate", (data) => {
      setCoinBalance(data.coinBalance);
      setNotificationMessage(`Số dư coin của bạn đã được cập nhật: ${data.coinBalance} coin`);
    });

    // Lắng nghe sự kiện membershipUpdate để cập nhật gói VIP hiện tại
    socket.on("membershipUpdate", (data) => {
      setCurrentMembership(data.membership);
      setNotificationMessage("Gói VIP của bạn đã được cập nhật!");
      setTimeout(() => setNotificationMessage(null), 3000);
    });

    socket.on("error", (error) => {
      setError(error.message || "Lỗi kết nối WebSocket!");
    });

    return () => {
      socket.off("notification");
      socket.off("coinBalanceUpdate");
      socket.off("membershipUpdate");
      socket.off("error");
    };
  }, [fetchCoinBalance, fetchCoinTransactions, fetchPaymentGateways, fetchExchangeRate, fetchCurrentMembership, fetchUpgradeRequests]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setError("Vui lòng đăng nhập để truy cập trang này!");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        await fetchCoinBalance();
        await fetchCoinTransactions();
        await fetchPaymentAccounts();
        await fetchPaymentGateways();
        await fetchExchangeRate();
        await fetchCoinToVipPlans();
        await fetchCurrentMembership();
        await fetchUpgradeRequests();
      } catch (error) {
        if (error.message.includes("500")) {
          setError("Lỗi server khi tải dữ liệu. Vui lòng thử lại sau!");
        } else if (error.message.includes("401")) {
          setError("Phiên làm việc hết hạn. Vui lòng đăng nhập lại.");
        } else {
          setError(error.message || "Không thể tải dữ liệu! Vui lòng thử lại sau.");
        }
        console.error("Lỗi khi lấy dữ liệu:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchData();
    }
  }, [
    fetchWithRetry,
    authLoading,
    user,
    fetchCoinBalance,
    fetchCoinTransactions,
    fetchPaymentAccounts,
    fetchPaymentGateways,
    fetchExchangeRate,
    fetchCoinToVipPlans,
    fetchCurrentMembership,
    fetchUpgradeRequests,
  ]);

  const handleAddPaymentAccount = async (e) => {
    e.preventDefault();
    try {
      if (!newPaymentGateway || !newPaymentAccountDetails) {
        setError("Vui lòng chọn cổng thanh toán và nhập thông tin tài khoản!");
        return;
      }

      const isValidGateway = paymentGateways.some(gateway => gateway.name === newPaymentGateway);
      if (!isValidGateway) {
        setError(`Cổng thanh toán '${newPaymentGateway}' không được hỗ trợ hoặc chưa được kích hoạt! Vui lòng chọn cổng hợp lệ.`);
        return;
      }

      const res = await fetchWithRetry({
        method: "put",
        url: "/api/users/update-payment-account",
        data: {
          gateway: newPaymentGateway,
          accountDetails: newPaymentAccountDetails,
        },
      });
      setNotificationMessage(res.data.message || "Thêm tài khoản thanh toán thành công!");
      setNewPaymentGateway("");
      setNewPaymentAccountDetails("");
      fetchPaymentAccounts();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      setError(error.response?.data?.message || "Lỗi khi thêm tài khoản thanh toán!");
    }
  };

  const handleDeletePaymentAccount = async (gateway) => {
    try {
      const res = await fetchWithRetry({
        method: "delete",
        url: "/api/users/delete-payment-account",
        data: { gateway },
      });
      setNotificationMessage(res.data.message || "Xóa tài khoản thanh toán thành công!");
      fetchPaymentAccounts();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      setError(error.response?.data?.message || "Lỗi khi xóa tài khoản thanh toán!");
    }
  };

  const handleWithdrawalRequest = async (e) => {
    e.preventDefault();
    try {
      if (!withdrawalAmount || !withdrawalMethod) {
        setError("Vui lòng nhập số coin và chọn phương thức thanh toán!");
        return;
      }

      if (parseInt(withdrawalAmount) <= 0) {
        setError("Số coin muốn rút phải lớn hơn 0!");
        return;
      }

      if (parseInt(withdrawalAmount) > coinBalance) {
        setError("Số coin muốn rút vượt quá số dư hiện tại!");
        return;
      }

      if (!exchangeRate || exchangeRate === 0) {
        setError("Giá trị quy đổi chưa được thiết lập. Vui lòng liên hệ quản trị viên!");
        return;
      }

      const isValidMethod = paymentGateways.some(gateway => gateway.name === withdrawalMethod);
      if (!isValidMethod) {
        setError(`Phương thức thanh toán '${withdrawalMethod}' không được hỗ trợ hoặc chưa được kích hoạt! Vui lòng chọn phương thức hợp lệ.`);
        return;
      }

      const res = await fetchWithRetry({
        method: "post",
        url: "/api/users/request-coin-withdrawal",
        data: {
          coinAmount: parseInt(withdrawalAmount),
          paymentMethod: withdrawalMethod,
        },
      });
      setNotificationMessage(res.data.message || "Yêu cầu rút tiền đã được gửi!");
      setShowWithdrawalForm(false);
      setWithdrawalAmount("");
      setWithdrawalMethod("");
      fetchCoinBalance();
      fetchCoinTransactions();
      setTimeout(() => setNotificationMessage(null), 3000);
    } catch (error) {
      setError(error.response?.data?.message || "Lỗi khi gửi yêu cầu rút tiền! Vui lòng thử lại sau.");
      console.error("Lỗi khi gửi yêu cầu rút tiền:", error);
    }
  };

  if (loading || authLoading) {
    return <p className="text-center">Đang tải...</p>;
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <h2 className="text-4xl font-extrabold text-gray-900 mb-8 text-center">Quản lý Coin</h2>

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          {error.includes("Phiên làm việc hết hạn") ? (
            <button
              onClick={() => navigate("/login")}
              className="ml-4 px-4 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200"
            >
              Đăng nhập lại
            </button>
          ) : (
            <button
              onClick={() => setError(null)}
              className="ml-4 px-4 py-1 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition duration-200"
            >
              Đóng
            </button>
          )}
        </div>
      )}

      {notificationMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg">{notificationMessage}</div>
      )}

      <div className="mb-8">
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setActiveTab("management")}
            className={`py-2 px-4 font-semibold ${activeTab === "management" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"}`}
          >
            Quản lý Coin
          </button>
          <button
            onClick={() => setActiveTab("upgrade")}
            className={`py-2 px-4 font-semibold ${activeTab === "upgrade" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"}`}
          >
            Đổi Coin thành gói VIP
          </button>
          <button
            onClick={() => setActiveTab("upgradeHistory")}
            className={`py-2 px-4 font-semibold ${activeTab === "upgradeHistory" ? "border-b-2 border-blue-500 text-blue-500" : "text-gray-500"}`}
          >
            Lịch sử yêu cầu nâng cấp
          </button>
        </div>
      </div>

      {activeTab === "management" && (
        <>
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Số dư Coin của bạn</h3>
            <div className="p-6 bg-white rounded-xl shadow-lg flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <FaCoins className="text-yellow-500 w-8 h-8" />
                <p className="text-gray-700 text-lg">
                  <strong>Số dư:</strong> {coinBalance} Coin
                </p>
              </div>
              <button
                onClick={() => setShowWithdrawalForm(true)}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition duration-200 disabled:bg-gray-300"
                disabled={paymentAccounts.length === 0 || !exchangeRate}
              >
                Rút tiền
              </button>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Thiết lập tài khoản thanh toán</h3>
            <div className="p-6 bg-white rounded-xl shadow-lg">
              <form onSubmit={handleAddPaymentAccount} className="mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Cổng thanh toán</label>
                    <select
                      value={newPaymentGateway}
                      onChange={(e) => setNewPaymentGateway(e.target.value)}
                      className="w-full p-2 border rounded-lg"
                      required
                      disabled={paymentGateways.length === 0}
                    >
                      <option value="">Chọn cổng thanh toán</option>
                      {paymentGateways.map((gateway) => (
                        <option key={gateway.name} value={gateway.name}>
                          {gateway.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Thông tin tài khoản</label>
                    <input
                      type="text"
                      value={newPaymentAccountDetails}
                      onChange={(e) => setNewPaymentAccountDetails(e.target.value)}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Nhập thông tin tài khoản"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-300"
                  disabled={paymentGateways.length === 0}
                >
                  Thêm tài khoản thanh toán
                </button>
              </form>

              {paymentAccounts.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-x-auto"
                >
                  <table className="w-full bg-white rounded-lg shadow-lg">
                    <thead>
                      <tr className="bg-blue-500 text-white">
                        <th className="p-4 text-left">Cổng thanh toán</th>
                        <th className="p-4 text-left">Thông tin tài khoản</th>
                        <th className="p-4 text-left">Hành động</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentAccounts.map((account) => (
                        <tr key={account._id} className="border-b hover:bg-gray-50">
                          <td className="p-4">{paymentGateways.find(g => g.name === account.gateway)?.label || account.gateway}</td>
                          <td className="p-4">{account.accountDetails}</td>
                          <td className="p-4">
                            <button
                              onClick={() => handleDeletePaymentAccount(account.gateway)}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              Xóa
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </motion.div>
              ) : (
                <p className="text-gray-600 text-center">Bạn chưa thiết lập tài khoản thanh toán nào.</p>
              )}
            </div>
          </div>

          {showWithdrawalForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h2 className="text-xl font-semibold mb-4">Yêu cầu rút tiền</h2>
                <form onSubmit={handleWithdrawalRequest}>
                  <div className="mb-4">
                    <label className="block text-gray-700 font-medium mb-2">Số coin muốn rút</label>
                    <input
                      type="number"
                      value={withdrawalAmount}
                      onChange={(e) => setWithdrawalAmount(e.target.value)}
                      className="w-full p-2 border rounded-lg"
                      placeholder="Nhập số coin"
                      required
                    />
                    {exchangeRate && withdrawalAmount ? (
                      <p className="text-gray-600 mt-2">
                        Số tiền nhận được: {(withdrawalAmount * exchangeRate).toLocaleString()} VND
                      </p>
                    ) : (
                      <p className="text-gray-600 mt-2">Không thể tính số tiền nhận được do giá trị quy đổi chưa được thiết lập.</p>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 font-medium mb-2">Phương thức thanh toán</label>
                    <select
                      value={withdrawalMethod}
                      onChange={(e) => setWithdrawalMethod(e.target.value)}
                      className="w-full p-2 border rounded-lg"
                      required
                    >
                      <option value="">Chọn phương thức</option>
                      {paymentAccounts.map((account) => {
                        const gateway = paymentGateways.find(g => g.name === account.gateway);
                        if (gateway) {
                          return (
                            <option key={account._id} value={account.gateway}>
                              {gateway.label || account.gateway}
                            </option>
                          );
                        }
                        return null;
                      })}
                    </select>
                  </div>
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={() => setShowWithdrawalForm(false)}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      Hủy
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                      disabled={!exchangeRate}
                    >
                      Gửi yêu cầu
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Lịch sử giao dịch Coin</h3>
            {coinTransactions.length > 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="overflow-x-auto"
              >
                <table className="w-full bg-white rounded-lg shadow-lg">
                  <thead>
                    <tr className="bg-blue-500 text-white">
                      <th className="p-4 text-left">Loại giao dịch</th>
                      <th className="p-4 text-left">Số Coin</th>
                      <th className="p-4 text-left">Mô tả</th>
                      <th className="p-4 text-left">Ngày giao dịch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coinTransactions.map((transaction) => (
                      <tr key={transaction._id} className="border-b hover:bg-gray-50 transition duration-200">
                        <td className="p-4 text-gray-700">
                          {transaction.type === "purchase"
                            ? "Mua Coin"
                            : transaction.type === "refund"
                            ? "Hoàn tiền"
                            : transaction.type === "gift"
                            ? "Gửi quà"
                            : transaction.type === "withdrawal"
                            ? "Rút tiền"
                            : "Nâng cấp VIP"}
                        </td>
                        <td className={`p-4 ${transaction.coinAmount >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {transaction.coinAmount > 0 ? "+" : ""}
                          {transaction.coinAmount} Coin
                        </td>
                        <td className="p-4 text-gray-700">{transaction.description}</td>
                        <td className="p-4 text-gray-700">{new Date(transaction.createdAt).toLocaleString("vi-VN")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            ) : (
              <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
                Bạn chưa có giao dịch Coin nào.
              </p>
            )}
          </div>
        </>
      )}

      {activeTab === "upgrade" && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Đổi Coin thành gói VIP</h3>

          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Gói VIP hiện tại</h4>
            <div className="p-4 bg-white rounded-xl shadow-lg">
              {currentMembership ? (
                <div>
                  <p className="text-gray-700">
                    <strong>Gói:</strong> {currentMembership.plan?.name || "Không xác định"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Trạng thái:</strong> {currentMembership.status === "active" ? "Đang hoạt động" : "Hết hạn"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Ngày bắt đầu:</strong> {new Date(currentMembership.startDate).toLocaleDateString("vi-VN")}
                  </p>
                  <p className="text-gray-700">
                    <strong>Ngày hết hạn:</strong> {new Date(currentMembership.endDate).toLocaleDateString("vi-VN")}
                  </p>
                </div>
              ) : (
                <p className="text-gray-600">Bạn chưa đăng ký gói VIP nào.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.length > 0 && Array.isArray(plans) ? (
              plans.map((plan) => {
                const currentPlanLevel = currentMembership && currentMembership.plan?.name
                  ? getPlanLevel(currentMembership.plan.name)
                  : 0;
                const targetPlanLevel = getPlanLevel(plan.name);
                const isActiveMembership = currentMembership && currentMembership.status === "active" && new Date(currentMembership.endDate) > new Date();
                const canUpgrade = !isActiveMembership || (isActiveMembership && targetPlanLevel > currentPlanLevel);

                return (
                  <div key={plan._id} className="p-6 bg-white rounded-xl shadow-lg">
                    <h4 className="text-2xl font-semibold text-gray-800 mb-4" style={{ display: "block" }}>
                      {plan.name || "Gói không xác định"}
                    </h4>
                    <p className="text-gray-600">Thời hạn: {plan.duration || 0} tháng</p>
                    <p className="text-gray-600 font-semibold mt-2">
                      Số coin cần thiết: {plan.coinPrice ? plan.coinPrice.toLocaleString() : "Không hỗ trợ"} coin
                    </p>
                    {plan.coinDiscount > 0 && plan.discountStartDate && plan.discountEndDate && (
                      <p className="text-green-600">
                        Giảm giá: {plan.coinDiscount}% (Từ {new Date(plan.discountStartDate).toLocaleDateString("vi-VN")} đến{" "}
                        {new Date(plan.discountEndDate).toLocaleDateString("vi-VN")})
                      </p>
                    )}
                    {Array.isArray(plan.benefits) && plan.benefits.length > 0 ? (
                      <ul className="list-disc pl-5 mt-4">
                        {plan.benefits.map((benefit, index) => (
                          <li key={index} className="text-gray-600">{benefit}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-600 mt-4">Không có quyền lợi nào được liệt kê.</p>
                    )}
                    <button
                      onClick={() => handleUpgradeWithCoin(plan._id, plan.name || "Gói không xác định", plan.coinPrice)}
                      disabled={
                        !plan.coinPrice ||
                        plan.coinPrice <= 0 ||
                        coinBalance < plan.coinPrice ||
                        !canUpgrade
                      }
                      className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200 disabled:bg-gray-300"
                    >
                      Đổi coin để nâng cấp
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
                Hiện tại chưa có gói VIP nào hỗ trợ đổi bằng coin.
              </p>
            )}
          </div>
        </div>
      )}

      {activeTab === "upgradeHistory" && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Lịch sử yêu cầu nâng cấp gói VIP</h3>
          {upgradeRequests.length > 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="overflow-x-auto"
            >
              <table className="w-full bg-white rounded-lg shadow-lg">
                <thead>
                  <tr className="bg-blue-500 text-white">
                    <th className="p-4 text-left">Gói VIP</th>
                    <th className="p-4 text-left">Số coin</th>
                    <th className="p-4 text-left">Trạng thái</th>
                    <th className="p-4 text-left">Ngày yêu cầu</th>
                  </tr>
                </thead>
                <tbody>
                  {upgradeRequests.map((request) => (
                    <tr key={request._id} className="border-b hover:bg-gray-50 transition duration-200">
                      <td className="p-4">{request.planId ? request.planId.name : "Gói không xác định"}</td>
                      <td className="p-4">{request.coinAmount.toLocaleString()}</td>
                      <td className="p-4">
                        {request.status === "pending" ? "Chờ xử lý" : request.status === "approved" ? "Đã phê duyệt" : "Đã từ chối"}
                      </td>
                      <td className="p-4">{new Date(request.createdAt).toLocaleString("vi-VN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          ) : (
            <p className="text-gray-600 text-center p-6 bg-white rounded-xl shadow-lg">
              Bạn chưa có yêu cầu nâng cấp gói VIP nào.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CoinManagement;